import type { CategoryNode } from '~/types/category'

function sortByOrd(nodes: readonly CategoryNode[]): CategoryNode[] {
  return [...nodes].sort((a, b) => (a.ord ?? 0) - (b.ord ?? 0))
}

/** Depth-first flatten of a nested public category tree. Roots and siblings by `ord`. */
export function flattenCategoryTree(roots: readonly CategoryNode[]): CategoryNode[] {
  const out: CategoryNode[] = []
  const walk = (nodes: readonly CategoryNode[]) => {
    for (const node of sortByOrd(nodes)) {
      out.push(node)
      if (node.children?.length) walk(node.children)
    }
  }
  walk(roots)
  return out
}

export function mapBySlug(nodes: readonly CategoryNode[]): Map<string, CategoryNode> {
  const map = new Map<string, CategoryNode>()
  for (const node of nodes) {
    if (node.slug) map.set(node.slug, node)
  }
  return map
}

/** Path slugs that exist in the active tree (skips inactive ancestors). */
export function activePathNodes(
  path: readonly string[] | undefined,
  bySlug: Map<string, CategoryNode>,
): CategoryNode[] {
  if (!path?.length) return []
  const nodes: CategoryNode[] = []
  for (const slug of path) {
    const node = bySlug.get(slug)
    if (node) nodes.push(node)
  }
  return nodes
}

/** Shop product URLs use the active tree root, not the listing/assignment slug. */
export function shopRootSlug(
  slug: string,
  bySlug: Map<string, CategoryNode>,
): string {
  const node = bySlug.get(slug)
  return activePathNodes(node?.path, bySlug)[0]?.slug || node?.slug || slug
}

/**
 * Prefer assignment/listing slug when it resolves to a node in the public tree;
 * otherwise fall back so product links never target a missing category.
 */
export function canonicalShopRootSlug(
  preferred: string | null | undefined,
  fallback: string | null | undefined,
  bySlug: Map<string, CategoryNode>,
): string {
  const tryRoot = (slug: string | null | undefined) => {
    if (!slug) return null
    const root = shopRootSlug(slug, bySlug)
    return bySlug.has(root) ? root : null
  }
  return tryRoot(preferred) ?? tryRoot(fallback) ?? fallback ?? preferred ?? 'category'
}

/**
 * Tiles for CategoryChildNav: children when present, otherwise siblings
 * (parent's children, including the current leaf). Roots with no children → [].
 */
export function navTilesForCategory(
  current: CategoryNode | null | undefined,
  pathNodes: readonly CategoryNode[],
): CategoryNode[] {
  if (!current) return []
  if (current.children?.length) return sortByOrd(current.children)
  if (pathNodes.length < 2) return []
  const parent = pathNodes[pathNodes.length - 2]
  return sortByOrd(parent?.children ?? [])
}

/**
 * Scope category for the leading “All …” child-nav tile.
 * Null when there are no nav tiles (same emptiness as navTilesForCategory).
 */
export function navScopeCategory(
  current: CategoryNode | null | undefined,
  pathNodes: readonly CategoryNode[],
): CategoryNode | null {
  if (!current || !navTilesForCategory(current, pathNodes).length) return null
  if (current.children?.length) return current
  return pathNodes[pathNodes.length - 2] ?? null
}
